import { type FleetManagementTypes, consoleApi, useActiveProject } from '@nx/state';
import { isNonEmptyString } from '@nx/stdlib';
import { DataGridHelpers } from '@nx/ui';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { hasLicenseIssues } from '../../../../../packages/state/src/slices/fleet-management/types';
import { useTokens } from '../hooks/use-tokens';
import { DeploymentStatus } from '../shared/components/deployment-status';
import { UnmonitoredServersBadge, hasUnmonitoredServers } from '../shared/components/server-status';
import { UnknownUser } from '../shared/components/unknown-user';
import { TokenStatus } from '../token-management';
import { DeploymentActionMenu } from './deployment-action-menu';
import { LicenseIssueBadge } from './deployment-inspect-drawer';

export const dbmsTableHelper = createColumnHelper<FleetManagementTypes.DbmsDetails>();

type DbmsTableProps = {
  dbmss: FleetManagementTypes.DbmsDetails[];
  filter: string;
  setFilter: (filter: string) => void;
  handleDisplayDbmsDetails: (dbms: FleetManagementTypes.DbmsDetails) => void;
};

export const DbmsTable = ({ dbmss, filter, setFilter, handleDisplayDbmsDetails }: DbmsTableProps) => {
  const { tokens } = useTokens();

  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);

  const columns = useMemo(
    () => [
      dbmsTableHelper.accessor('name', {
        header: 'Name',
        size: 200,
        minSize: 100,
        filterFn: 'includesString',
        cell: (cx) => {
          const dbms = cx.row.original;
          return <DataGridHelpers.TruncatedColumn value={dbms.name} />;
        },
      }),
      dbmsTableHelper.accessor('status', {
        header: 'Status',
        size: 200,
        minSize: 100,
        cell: (cx) => {
          const dbms = cx.row.original;
          return (
            <div className="flex flex-col">
              <DeploymentStatus status={dbms.status} />
              <TokenStatus token={tokens.find((token) => token.claimed_by === dbms.dbmsId)} />
              {hasLicenseIssues(dbms) && <LicenseIssueBadge onClick={() => handleDisplayDbmsDetails(dbms)} />}
              {hasUnmonitoredServers(dbms) && <UnmonitoredServersBadge viewType="table" />}
            </div>
          );
        },
      }),
      dbmsTableHelper.accessor('version', {
        header: 'Version',
        size: 200,
        minSize: 100,
        filterFn: 'includesString',
      }),
      dbmsTableHelper.accessor('edition', {
        header: 'Edition',
        size: 200,
        minSize: 100,
        filterFn: 'includesString',
      }),
      dbmsTableHelper.accessor('packaging', {
        header: 'Packaging',
        size: 200,
        minSize: 50,
        filterFn: 'includesString',
      }),
      dbmsTableHelper.accessor(
        (row) => {
          return getProjectMembersRes.data?.find((member) => member.userId === row.tokenCreatedBy)?.email;
        },
        {
          header: 'Registered by',
          size: 200,
          minSize: 100,
          cell: (cx) => {
            const value = cx.getValue();
            if (isNonEmptyString(value)) {
              return <DataGridHelpers.TruncatedColumn value={value} />;
            }
            return <UnknownUser userId={cx.row.original.tokenCreatedBy} />;
          },
        },
      ),
      dbmsTableHelper.display({
        id: 'actions',
        cell: (cx) => {
          const dbms = cx.row.original;
          return <DeploymentActionMenu dbms={dbms} handleDisplayDbmsDetails={handleDisplayDbmsDetails} />;
        },
        size: 75,
        maxSize: 75,
        enableResizing: false,
      }),
    ],
    [getProjectMembersRes.data, handleDisplayDbmsDetails, tokens],
  );

  const table = useReactTable({
    data: dbmss,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilter,
    autoResetPageIndex: false,
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    state: {
      globalFilter: filter,
      columnPinning: {
        right: ['actions'],
      },
    },
  });

  return (
    <div className="border-neutral-border-weak flex flex-col gap-4 overflow-y-auto rounded-2xl border">
      <DataGridHelpers.DataGridRightColumnPinned<FleetManagementTypes.DbmsDetails>
        tableInstance={table}
        styling={{ headerStyle: 'filled', borderStyle: 'all-sides' }}
      />
    </div>
  );
};
